import { Component, ComponentRef, Directive, inject, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges, TemplateRef, ViewContainerRef } from "@angular/core";

export enum UIState {
  'default' = 'default',
  'inprogress' = 'inprogress',
  'error' = 'error'
}

export enum UIStateSpinnerSize {
  'small' = 'small',
  'medium' = 'medium',
  'large' = 'large'
}

@Component({
  selector: 'ui-state-inprogress',
  template: `
    <div class="ui-state-inprogress">
      <mat-spinner [diameter]="getSpinnerDiameter()"></mat-spinner>
    </div>
  `,
  styles: [`
    .ui-state-inprogress {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    }
  `]
})
export class UIStateSpinnerComponent {
  @Input() size: UIStateSpinnerSize;

  getSpinnerDiameter() {
    switch (this.size) {
      case UIStateSpinnerSize.small: return 24;
      case UIStateSpinnerSize.medium: return 32;
      default: return 48;
    }
  }
}

@Component({
  selector: 'ui-state-error',
  template: `<div class="ui-state-error">{{ message }}</div>`,
  styles: [`
    .ui-state-error {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
      color: #E57372;
    }
  `]
})
export class UIStateErrorComponent {
  @Input() message: string;
}

@Directive({ selector: '[state]' })
export class UIStateDirective implements OnChanges, OnDestroy {
  @Input() state = UIState.default;
  @Input('stateSpinnerSize') spinnerSize = UIStateSpinnerSize.medium;
  @Input('stateErrorMessage') errorMessage = 'Error, try again later';
  @Input('stateSpinnerStyles') spinnerStyles?: any; // Custom styles

  private wrapper?: HTMLElement;
  private overlay?: ComponentRef<unknown>;
  private readonly renderer = inject(Renderer2);
  private readonly view = this.vcr.createEmbeddedView(this.tpl);

  constructor(
    private readonly tpl: TemplateRef<unknown>,
    private readonly vcr: ViewContainerRef,
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    const stateChanged = changes['state'];
    const spinnerChanged = changes['spinnerSize'] && this.state === UIState.inprogress;
    const errorChanged = changes['errorMessage'] && this.state === UIState.error;

    if (stateChanged || spinnerChanged || errorChanged) {
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.destroyOverlay();
    this.destoryWrapper();
  }

  private render() {
    this.ensureWrapper();

    const roots = this.view.rootNodes as HTMLElement[];
    switch (this.state) {
      case UIState.inprogress:
        this.toggleVisibility(roots, false);
        this.showOverlay(UIStateSpinnerComponent, { size: this.spinnerSize });
        break;
      case UIState.error:
        this.toggleVisibility(roots, false);
        this.showOverlay(UIStateErrorComponent, { message: this.errorMessage });
        break;
      default:
        this.toggleVisibility(roots, true);
        this.destroyOverlay();
        break;
    }
  }

  private toggleVisibility(nodes: HTMLElement[], visible: boolean) {
    nodes.forEach(el =>
      this.renderer.setStyle(el, 'visibility', visible ? 'visible' : 'hidden')
    );
  }

  private showOverlay<T>(comp: new (...args: any[]) => T, inputs?: Partial<T>) {
    this.destroyOverlay();

    this.overlay = this.vcr.createComponent(comp);
    if (inputs) Object.assign(this.overlay.instance as any, inputs);

    const host = this.overlay.location.nativeElement as HTMLElement;
    this.renderer.setStyle(host, 'position', 'absolute');
    this.renderer.setStyle(host, 'inset', '0');
    this.renderer.setStyle(host, 'display', 'flex');
    this.renderer.setStyle(host, 'justifyContent', 'center');
    this.renderer.setStyle(host, 'alignItems', 'center');

    this.wrapper.appendChild(host);
  }

  private ensureWrapper(): void {
    if (this.wrapper) {
      const needsFix = this.view.rootNodes.some(
        n => n.parentNode !== this.wrapper
      );
      if (needsFix) {
        this.view.rootNodes.forEach(node =>
          this.wrapper!.appendChild(node)
        );
      }
      return;
    }

    const anchor = this.vcr.element.nativeElement as Comment;
    const wrapper = this.renderer.createElement('div');
    this.renderer.addClass(wrapper, 'state-wrapper');
    this.renderer.setStyle(wrapper, 'position', 'relative');
    if (this.spinnerStyles) {
      Object.entries(this.spinnerStyles).forEach(([key, value]) => {
        this.renderer.setStyle(wrapper, key, value);
      });
    }
    this.renderer.insertBefore(anchor.parentNode!, wrapper, anchor);
    this.view.rootNodes.forEach(node => wrapper.appendChild(node));
    this.wrapper = wrapper;
  }

  private destoryWrapper() {
    if (!this.wrapper) return;

    const parent = this.wrapper.parentNode;
    if (parent) {
      this.renderer.removeChild(parent, this.wrapper);
    }
    this.wrapper = undefined;
  }

  private destroyOverlay() {
    this.overlay?.destroy();
    this.overlay = undefined;
  }
}
